import { type } from 'arktype'

import { m } from '@orus.eu/message'
import { type Result, success } from '@orus.eu/result'

import {
  AbstractDimension,
  type DimensionnedState,
  type DimensionValidationProblem,
  dimensionValidationFailure,
  type LooselyTypedValue,
} from './abstract-dimension.ts'
import type { rcdaSelectedDimension } from './product-selection-dimensions.ts'

/**
 * Files names are sometimes in french given the context of the product and those documents are specific to french regulation
 * WARNING - Only add values to this list
 */
export const ALL_GROUP_CATEGORIES = [
  'id',
  'immatriculation',
  'infoReport',
  'proformaInvoice',
  'invoices',
  'RIB',
  'experience',
  'experiencePizzeria',
  'yearlyAccount',
  'decennaleCertificate',
  'other',
  'acag',
  'uncategorized',
  'bankingInformation',
  'attestations',
  'insurance_history',
  'professionalCard',
  'vehicleRegistration',
  'driverLicense',
  'taxiLicense',
  'lotiLicense',
  'umbrellaContract',
  'driverIdentity',
  'leasingContract',
  'representativeIdentity',
  'premisesLease',
  'wGarageCertificate',
] as const

export const subscriptionDocumentValidationStatus = [
  'none',
  'pending',
  'received',
  'insuranceReview',
  'incomplete',
  'rejected',
  'validated',
] as const
export const subscriptionDocumentValidationStatusSchema = type('===', ...subscriptionDocumentValidationStatus)
export type SubscriptionDocumentValidationStatus = typeof subscriptionDocumentValidationStatusSchema.infer

export function isSubscriptionValidationStatus(value: string): value is SubscriptionDocumentValidationStatus {
  return subscriptionDocumentValidationStatusSchema.allows(value)
}

export const groupCategorySchema = type('===', ...ALL_GROUP_CATEGORIES)
export type GroupCategory = typeof groupCategorySchema.infer

export function isGroupCategory(value: string): value is GroupCategory {
  return groupCategorySchema.allows(value)
}

export const AI_ANALYZED_DOCUMENT_CATEGORIES = ['id', 'immatriculation', 'invoices'] as const satisfies GroupCategory[]
export type AiAnalyzedDocumentCategory = (typeof AI_ANALYZED_DOCUMENT_CATEGORIES)[number]

export const subscriptionDocumentFileSchema = type({
  name: 'string',
  id: 'string',
  mimeType: 'string',
  creationTimestamp: 'number',
  'description?': 'string | null | undefined',
})

export const subscriptionDocumentNoteSchema = type({
  notes: 'string',
  noteTimestamp: 'number',
})

export const subscriptionDocumentSchema = type({
  files: subscriptionDocumentFileSchema.array(),
  status: subscriptionDocumentValidationStatusSchema,
  'notes?': subscriptionDocumentNoteSchema.or(type.undefined),
})

export type SubscriptionDocumentFile = typeof subscriptionDocumentFileSchema.infer

export type SubscriptionDocumentNote = typeof subscriptionDocumentNoteSchema.infer

export type SubscriptionDocument = typeof subscriptionDocumentSchema.infer

export class SubscriptionDocumentDimension<NAME extends string> extends AbstractDimension<NAME, SubscriptionDocument> {
  override validateData(value: LooselyTypedValue): Result<SubscriptionDocument, DimensionValidationProblem> {
    const out = subscriptionDocumentSchema(value)
    if (out instanceof type.errors) {
      return dimensionValidationFailure(`Field ${this.name} is not an SubscriptionDocument : ${out.summary}`)
    }

    return success(out)
  }

  override formatToString(value: SubscriptionDocument): string | undefined {
    return `Files: ${value.files.map((file) => file.name).join(', ')}\nStatus: ${value.status}`
  }
}

export const idDocumentDimension = new SubscriptionDocumentDimension({
  name: 'idDocument',
  displayKeys: { name: 'dimension_name_id_document' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const immatriculationDocumentDimension = new SubscriptionDocumentDimension({
  name: 'immatriculationDocument',
  displayKeys: { name: 'dimension_name_immatriculation_document' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const infoReportDocumentDimension = new SubscriptionDocumentDimension({
  name: 'infoReportDocument',
  displayKeys: { name: 'dimension_name_info_report_document' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const proformaInvoiceDocumentDimension = new SubscriptionDocumentDimension({
  name: 'proformaInvoiceDocument',
  displayKeys: { name: 'dimension_name_proforma_invoice_document' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const invoicesDocumentDimension = new SubscriptionDocumentDimension({
  name: 'invoicesDocument',
  displayKeys: { name: 'dimension_name_invoices_document' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const yearlyAccountsDocumentDimension = new SubscriptionDocumentDimension({
  name: 'yearlyAccountsDocument',
  displayKeys: { name: 'dimension_name_yearly_accounts_document' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const experienceDocumentDimension = new SubscriptionDocumentDimension({
  name: 'experienceDocument',
  displayKeys: { name: 'dimension_name_experience_document' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const experiencePizzeriaDocumentDimension = new SubscriptionDocumentDimension({
  name: 'experiencePizzeriaDocument',
  displayKeys: { name: 'dimension_name_experience_pizzeria_document' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const ribDocumentDimension = new SubscriptionDocumentDimension({
  name: 'ribDocument',
  displayKeys: { name: 'dimension_name_rib_document' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const decennaleCertificateDocumentDimension = new SubscriptionDocumentDimension({
  name: 'decennaleCertificateDocument',
  displayKeys: { name: 'dimension_name_decennale_certificate_document' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const otherDocumentDimension = new SubscriptionDocumentDimension({
  name: 'otherDocument',
  displayKeys: { name: 'dimension_name_other_document' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const acagDocumentDimension = new SubscriptionDocumentDimension({
  name: 'acagDocument',
  displayKeys: { name: 'dimension_name_acag_document' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const professionalCardDocumentDimension = new SubscriptionDocumentDimension({
  name: 'professionalCardDocument',
  displayKeys: { name: 'dimension_name_professional_card_document' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const vehicleRegistrationDocumentDimension = new SubscriptionDocumentDimension({
  name: 'vehicleRegistrationDocument',
  displayKeys: { name: 'dimension_name_vehicle_registration_document' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const driverLicenseDocumentDimension = new SubscriptionDocumentDimension({
  name: 'driverLicenseDocument',
  displayKeys: { name: 'dimension_name_driver_license_document' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const driverIdentityDocumentDimension = new SubscriptionDocumentDimension({
  name: 'driverIdentityDocument',
  displayKeys: { name: 'dimension_name_driver_identity_document' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const leasingContractDocumentDimension = new SubscriptionDocumentDimension({
  name: 'leasingContractDocument',
  displayKeys: { name: 'dimension_name_leasing_contract_document' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const representativeIdentityDocumentDimension = new SubscriptionDocumentDimension({
  name: 'representativeIdentityDocument',
  displayKeys: { name: 'dimension_name_representative_identity_document' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const premisesLeaseDocumentDimension = new SubscriptionDocumentDimension({
  name: 'premisesLeaseDocument',
  displayKeys: { name: 'dimension_name_premises_lease_document' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const wGarageCertificateDocumentDimension = new SubscriptionDocumentDimension({
  name: 'wGarageCertificateDocument',
  displayKeys: { name: 'dimension_name_w_garage_certificate_document' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const taxiLicenseDocumentDimension = new SubscriptionDocumentDimension({
  name: 'taxiLicenseDocument',
  displayKeys: { name: 'dimension_name_taxi_license_document' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const lotiLicenseDocumentDimension = new SubscriptionDocumentDimension({
  name: 'lotiLicenseDocument',
  displayKeys: { name: 'dimension_name_loti_license_document' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const umbrellaContractDocumentDimension = new SubscriptionDocumentDimension({
  name: 'umbrellaContractDocument',
  displayKeys: { name: 'dimension_name_umbrella_contract_document' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const uncategorizedDocumentDimension = new SubscriptionDocumentDimension({
  name: 'uncategorizedDocument',
  displayKeys: { name: 'dimension_name_uncategorized_document' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const documentsDimensions = [
  idDocumentDimension,
  immatriculationDocumentDimension,
  infoReportDocumentDimension,
  proformaInvoiceDocumentDimension,
  invoicesDocumentDimension,
  yearlyAccountsDocumentDimension,
  experienceDocumentDimension,
  decennaleCertificateDocumentDimension,
  ribDocumentDimension,
  otherDocumentDimension,
  experiencePizzeriaDocumentDimension,
  acagDocumentDimension,
  uncategorizedDocumentDimension,
  professionalCardDocumentDimension,
  vehicleRegistrationDocumentDimension,
  driverLicenseDocumentDimension,
  taxiLicenseDocumentDimension,
  lotiLicenseDocumentDimension,
  umbrellaContractDocumentDimension,
  driverIdentityDocumentDimension,
  leasingContractDocumentDimension,
  representativeIdentityDocumentDimension,
  premisesLeaseDocumentDimension,
  wGarageCertificateDocumentDimension,
] as const
export type DocumentsState = DimensionnedState<typeof documentsDimensions>

export const groupCategoryDimensionByCategory: Record<
  GroupCategory,
  SubscriptionDocumentDimension<keyof DocumentsState>
> = {
  id: idDocumentDimension,
  immatriculation: immatriculationDocumentDimension,
  infoReport: infoReportDocumentDimension,
  proformaInvoice: proformaInvoiceDocumentDimension,
  invoices: invoicesDocumentDimension,
  yearlyAccount: yearlyAccountsDocumentDimension,
  experience: experienceDocumentDimension,
  experiencePizzeria: experiencePizzeriaDocumentDimension,
  decennaleCertificate: decennaleCertificateDocumentDimension,
  RIB: ribDocumentDimension,
  other: otherDocumentDimension,
  acag: acagDocumentDimension,
  uncategorized: uncategorizedDocumentDimension,
  bankingInformation: ribDocumentDimension,
  attestations: otherDocumentDimension,
  insurance_history: infoReportDocumentDimension,
  professionalCard: professionalCardDocumentDimension,
  vehicleRegistration: vehicleRegistrationDocumentDimension,
  driverLicense: driverLicenseDocumentDimension,
  taxiLicense: taxiLicenseDocumentDimension,
  lotiLicense: lotiLicenseDocumentDimension,
  umbrellaContract: umbrellaContractDocumentDimension,
  driverIdentity: driverIdentityDocumentDimension,
  leasingContract: leasingContractDocumentDimension,
  representativeIdentity: representativeIdentityDocumentDimension,
  premisesLease: premisesLeaseDocumentDimension,
  wGarageCertificate: wGarageCertificateDocumentDimension,
}

export const subscriptionReviewFinishedDocumentsStatusEnumValueSpecification = m.nullable(
  m.enum<SubscriptionDocumentValidationStatus>(
    {
      title: 'Document reviewed status',
    },
    {
      none: 'Default status',
      pending: 'En attente de reception de documents',
      received: 'De nouveaux documents sont à vérifier',
      insuranceReview: 'À faire vérifier par les souscripteurs décennale',
      incomplete: 'Des documents sont manquants',
      rejected: 'L’ensemble des pièces a été refusé',
      validated: 'Tous les documents sont conformes',
    },
  ),
)

export type SubscriptionReviewFinishedDocumentsStatusSpec = Record<
  GroupCategory,
  typeof subscriptionReviewFinishedDocumentsStatusEnumValueSpecification
>

export const subscriptionReviewFinishedDocumentsNoteSpecification = m.nullable(
  m.string({ title: 'Note relative au document' }),
)

export type GroupCategoryNoteKey = GroupCategory extends GroupCategory ? `note_${GroupCategory}` : never

export type SubscriptionReviewFinishedDocumentsNoteSpec = Record<
  GroupCategoryNoteKey,
  typeof subscriptionReviewFinishedDocumentsNoteSpecification
>

export const subscriptionAcceptedDocumentsDescription: Record<GroupCategory, string[]> = {
  id: ['Carte d’identité, passeport, permis de conduire, carte de séjour.'],
  immatriculation: ['Extrait Kbis, Fiche INSEE, Fiche INPI.'],
  infoReport: ['Relevé d’informations, relevé de sinistralité (délivré par votre précédente assurance).'],
  proformaInvoice: ['Facture vierge de votre société.'],
  invoices: [
    'Factures clients.',
    'Pour les entreprises de moins de 6 mois les factures, devis vierge ou papier en-tête suffisent.',
  ],
  RIB: ['RIB.'],
  experience: [
    "Certificat de travail, bulletin de paie, attestation employeur : minimum 1 an d'expérience cumulé sur les 5 dernières années.",
  ],
  experiencePizzeria: ['Certificat de travail, bulletin de paie, attestation employeur'],
  yearlyAccount: ['Bilan comptable ou déclaration URSSAF.'],
  decennaleCertificate: ['Précédente attestation d’assurance décennale.'],
  other: ['Tout autre type de documents (exemple : Diplôme). '],
  acag: ['Une attestation de Couverture des activités à garantir fournie par Orus et signée'],
  uncategorized: ['Documents non catégorisés.'],
  bankingInformation: ['RIB.'],
  attestations: ['Attestations diverses.'],
  insurance_history: ["Relevé d'informations, relevé de sinistralité."],
  professionalCard: ['Carte professionnelle VTC ou carte de conducteur de taxi, en cours de validité.'],
  vehicleRegistration: ['Carte grise du véhicule.'],
  driverLicense: ['Permis de conduire.'],
  taxiLicense: ['Autorisation de stationnement (ADS), dite licence de taxi.'],
  lotiLicense: ['Licence LOTI.'],
  umbrellaContract: [
    'KBis de moins de 3 mois au nom de la société de portage.',
    'Contrat de portage entre le souscripteur et la société de portage.',
  ],
  driverIdentity: ['Carte d’identité, passeport, carte de séjour ou carte de résident du conducteur désigné.'],
  leasingContract: ['Contrat de leasing ou de location longue durée du véhicule.'],
  representativeIdentity: ['Carte d’identité, passeport, carte de séjour ou carte de résident du représentant légal.'],
  premisesLease: ['Bail commercial ou professionnel des locaux du garage.'],
  wGarageCertificate: ['Carte W garage de chaque plaque W déclarée, au nom du garage.'],
}

export const subscriptionEmailAcceptedDocumentsDescription: Record<GroupCategory, string[]> = {
  id: ['Carte d’identité, passeport, permis de conduire, carte de séjour.'],
  immatriculation: ['Extrait Kbis, Fiche INSEE, Fiche INPI (téléchargeable sur https://data.inpi.fr/).'],
  infoReport: [
    "Le relevé d'information de votre assurance précédente (vous pouvez leur demander directement) indiquant votre historique de sinistralité.",
  ],
  proformaInvoice: ['Facture vierge de votre société.'],
  invoices: [
    'Factures clients.',
    'Pour les entreprises de moins de 6 mois les factures, devis vierge ou papier en-tête suffisent.',
  ],
  RIB: ['Pour les virements et/ou régler des factures par avis de prélèvement.'],
  experience: ['Certificat de travail, bulletin de paie, attestation employeur.'],
  experiencePizzeria: ['Certificat de travail, bulletin de paie, attestation employeur'],
  yearlyAccount: ['Bilan comptable ou déclaration URSSAF.'],
  decennaleCertificate: ['Précédente attestation d’assurance décennale.'],
  other: ['Tout autre type de documents (exemple : Diplôme). '],
  acag: ['Une attestation de Couverture des activités à garantir fournie par Orus et signée'],
  uncategorized: ['Documents en attente de catégorisation.'],
  bankingInformation: ['Pour les virements et/ou régler des factures par avis de prélèvement.'],
  attestations: ['Attestations diverses.'],
  insurance_history: [
    "Le relevé d'information de votre assurance précédente (vous pouvez leur demander directement) indiquant votre historique de sinistralité.",
  ],
  professionalCard: ['Votre carte professionnelle VTC ou votre carte de conducteur de taxi, en cours de validité.'],
  vehicleRegistration: ['La carte grise du véhicule assuré.'],
  driverLicense: ['Votre permis de conduire.'],
  taxiLicense: ['Votre autorisation de stationnement (ADS), dite licence de taxi.'],
  lotiLicense: ['Votre licence LOTI.'],
  umbrellaContract: [
    'Le KBis de moins de 3 mois au nom de la société de portage.',
    'Le contrat de portage entre le souscripteur et la société de portage.',
  ],
  driverIdentity: ['La pièce d’identité du conducteur désigné.'],
  leasingContract: ['Le contrat de leasing ou de location longue durée du véhicule.'],
  representativeIdentity: ['La pièce d’identité du représentant légal de la société.'],
  premisesLease: ['Le bail des locaux de votre garage.'],
  wGarageCertificate: ['La carte W garage de chaque plaque W déclarée.'],
}

export const subscriptionEmailAlternativeAcceptedDocumentsDescription: Partial<Record<GroupCategory, string[]>> = {
  experience: [
    "Au moins 5 factures réparties sur les 4 derniers trimestres faisant mention de l'activité que vous souhaitez souscrire.",
  ],
}

export const subscriptionDocumentsEmoji: Record<GroupCategory, string> = {
  id: '🙋',
  immatriculation: '👔',
  infoReport: '📝',
  proformaInvoice: '🧾',
  invoices: '🧾',
  RIB: '💳',
  experience: '🎖️',
  experiencePizzeria: '🎖️',
  yearlyAccount: '📒',
  decennaleCertificate: '📄',
  other: '📁',
  acag: '📄',
  uncategorized: '⏳',
  bankingInformation: '💳',
  attestations: '📄',
  insurance_history: '📝',
  professionalCard: '🪪',
  vehicleRegistration: '🚗',
  driverLicense: '🪪',
  taxiLicense: '🚕',
  lotiLicense: '🚌',
  umbrellaContract: '📄',
  driverIdentity: '🙋',
  leasingContract: '📄',
  representativeIdentity: '🪪',
  premisesLease: '🏢',
  wGarageCertificate: '🔧',
}

/**
 * Defines the email address communicated to clients for subscription document related questions
 */
export function getSubscriptionDocumentEmail(state: DimensionnedState<[typeof rcdaSelectedDimension]>): string {
  return state.rcdaSelected
    ? // rcda is a special case which has it's own email address
      'construction@orus.eu'
    : // all other products are handled through the default support email address
      'hello@orus.eu'
}
